import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Layout from "../../components/Layout";
import MapModal from "./MapModal";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost/securecontrol-backend";

const COLORS = {
  primary: "#0B3C5D",
  secondary: "#1E88C8",
  accent: "#4FC3F7",
  darkText: "#2E2E2E",
  lightBg: "#F5F7FA",
  white: "#FFFFFF",
  danger: "#f44336",
  success: "#4caf50",
  warning: "#ff9800",
  info: "#2196f3",
  successLight: "#e8f5e9",
  dangerLight: "#ffebee",
  warningLight: "#fff3e0",
  infoLight: "#e3f2fd",
};

const statusLabel = (statut) => {
  switch (statut) {
    case 1:
      return {
        text: "En cours",
        className: "badge",
        style: { backgroundColor: "#fff3e0", color: "#f57c00" },
      };
    case 2:
    default:
      return {
        text: "Terminée",
        className: "badge",
        style: { backgroundColor: "#e8f5e9", color: "#2e7d32" },
      };
  }
};

const formatDate = (dateString) => {
  if (
    !dateString ||
    dateString === "0000-00-00" ||
    dateString === "0000-00-00 00:00:00"
  )
    return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

// Date locale du jour au format YYYY-MM-DD (pour défaut des filtres date) —
// new Date().toISOString() donne la date en UTC, ce qui décale le jour selon
// l'heure locale ; on reconstruit donc la date à partir des getters locaux.
const getTodayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const formatDateTime = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const RoundList = () => {
  const navigate = useNavigate();
  const [rondes, setRondes] = useState([]);
  const [filteredRondes, setFilteredRondes] = useState([]);
  const [societeInfo, setSocieteInfo] = useState(null);
  const [exportingPDF, setExportingPDF] = useState(false);

  // Logo + coordonnées de la société connectée, embarqués en en-tête des PDF exportés.
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) return;
    axios.get(`${API_BASE_URL}/societes/my_logo.php`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const infoLigne = [res.data?.address_societe, res.data?.tel_societe].filter(Boolean).join(" — ");
        setSocieteInfo({
          logo: res.data?.logo_base64 || null,
          nom: res.data?.nom_societe || null,
          infoLigne: infoLigne || null,
        });
      })
      .catch(() => {});
  }, []);
  const [loading, setLoading] = useState(true);
  const [serverError, setServerError] = useState("");
  const [infoMsg, setInfoMsg] = useState("");

  // ── État carte ──
  const [selectedRonde, setSelectedRonde] = useState(null);
  const [selectedSites, setSelectedSites] = useState([]);
  const [showMap, setShowMap] = useState(false);
  const [mapLoading, setMapLoading] = useState(false); // spinner sur le bouton

  const [filterControleur, setFilterControleur] = useState("");
  // Par défaut, la journée en cours (comparaison directe avec la date ISO
  // renvoyée par le backend, voir le correctif de format ci-dessous).
  const [filterDateDebut, setFilterDateDebut] = useState(getTodayISO());
  const [filterDateFin, setFilterDateFin] = useState(getTodayISO());
  const [filterStatut, setFilterStatut] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [userInfo] = useState(() => {
    try {
      const s = localStorage.getItem("user");
      return s ? JSON.parse(s) : null;
    } catch {
      return null;
    }
  });

  const [controllers, setControllers] = useState([]);
  const [controllersLoading, setControllersLoading] = useState(false);

  const fetchRondes = useCallback(async () => {
    setLoading(true);
    setServerError("");
    setInfoMsg("");
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setServerError("Session expirée.");
        setTimeout(() => navigate("/login"), 2000);
        return;
      }
      const response = await axios.get(
        `${API_BASE_URL}/rondes/liste_rondes.php`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        },
      );
      if (response.data?.success) {
        const normalized = (response.data.data || []).map((r) => ({
          ...r,
          id: r.id_ronde,
        }));
        setRondes(normalized);
        setFilteredRondes(normalized);
        if (normalized.length === 0) setInfoMsg("Aucune ronde trouvée.");
      } else {
        setServerError(
          response.data?.message ||
            "Impossible de charger la liste des rondes.",
        );
      }
    } catch (err) {
      handleFetchError(err);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const fetchControllers = useCallback(async () => {
    setControllersLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      if (!token) return;
      const response = await axios.get(
        `${API_BASE_URL}/users/list.php?profile=2`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (response.data?.success && Array.isArray(response.data.users)) {
        setControllers(response.data.users);
      }
    } catch (err) {
      console.error("Erreur contrôleurs:", err);
    } finally {
      setControllersLoading(false);
    }
  }, []);

  const handleFetchError = (err) => {
    if (err.response?.status === 401) {
      setServerError("Session expirée.");
      localStorage.removeItem("access_token");
      setTimeout(() => navigate("/login"), 2000);
    } else if (err.response?.status === 403) {
      setServerError("Accès interdit.");
    } else if (err.response?.status === 500) {
      setServerError("Erreur serveur.");
    } else if (err.request) {
      setServerError("Serveur inaccessible.");
    } else {
      setServerError("Erreur technique.");
    }
  };

  useEffect(() => {
    fetchRondes();
    fetchControllers();
  }, [fetchRondes, fetchControllers]);

  useEffect(() => {
    if (rondes.length > 0) {
      let filtered = [...rondes];
      if (filterControleur) {
        filtered = filtered.filter((r) =>
          getControleurName(r.controleur)
            .toLowerCase()
            .includes(filterControleur.toLowerCase()),
        );
      }
      if (filterDateDebut || filterDateFin) {
        // r.date_ronde est une chaîne "YYYY-MM-DD HH:MM:SS" côté backend :
        // comparer directement le préfixe ISO plutôt que de passer par
        // formatDate() (qui renvoie du "JJ/MM/AAAA") évite un mismatch de
        // format qui rendait ce filtre totalement inopérant auparavant.
        filtered = filtered.filter((r) => {
          const d = (r.date_ronde || "").slice(0, 10);
          if (!d) return false;
          if (filterDateDebut && d < filterDateDebut) return false;
          if (filterDateFin && d > filterDateFin) return false;
          return true;
        });
      }
      if (filterStatut) {
        filtered = filtered.filter((r) =>
          statusLabel(r.statut)
            .text.toLowerCase()
            .includes(filterStatut.toLowerCase()),
        );
      }
      if (searchTerm) {
        const s = searchTerm.toLowerCase();
        filtered = filtered.filter(
          (r) =>
            getControleurName(r.controleur).toLowerCase().includes(s) ||
            (r.site_count?.toString() || "").includes(s) ||
            formatDate(r.date_ronde).toLowerCase().includes(s) ||
            statusLabel(r.statut).text.toLowerCase().includes(s),
        );
      }
      setFilteredRondes(filtered);
      setCurrentPage(1);
      setInfoMsg(
        filtered.length === 0 ? "Aucune ronde ne correspond aux critères." : "",
      );
    }
  }, [rondes, filterControleur, filterDateDebut, filterDateFin, filterStatut, searchTerm]);

  const handleViewDetails = (ronde) => {
    const rondeId = ronde.id_ronde || ronde.id;
    if (!rondeId) {
      alert("ID de ronde invalide");
      return;
    }
    navigate(`/rondes/${rondeId}`);
  };

  // ── Fetch les sites de la ronde PUIS ouvre la modal ──
  const handleShowMap = async (ronde) => {
    const rondeId = ronde.id_ronde || ronde.id;
    if (!rondeId) {
      alert("ID de ronde invalide.");
      return;
    }

    setMapLoading(rondeId); // active le spinner sur ce bouton précis
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get(
        `${API_BASE_URL}/rondes/detail_rondes.php?id=${rondeId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
          timeout: 10000,
        },
      );

      if (response.data?.success && response.data.data) {
        const rondeDetail = response.data.data;
        const sitesWithGPS = (rondeDetail.sites || []).filter(
          (s) => s.latitude && s.longitude,
        );

        if (sitesWithGPS.length === 0) {
          alert("Aucun site avec coordonnées GPS pour cette ronde.");
          return;
        }

        setSelectedRonde({
          ...ronde,
          ...rondeDetail,
          formattedDate: formatDateTime(ronde.date_ronde),
        });
        setSelectedSites(sitesWithGPS);
        setShowMap(true);
      } else {
        alert("Impossible de charger les sites de cette ronde.");
      }
    } catch (err) {
      console.error("Erreur chargement sites:", err);
      alert("Erreur lors du chargement des sites. Vérifiez votre connexion.");
    } finally {
      setMapLoading(null);
    }
  };

  const getControleurName = (controleur) => {
    if (!controleur) return "N/A";
    if (controleur.nom_complet) return controleur.nom_complet;
    if (controleur.nom && controleur.prenom)
      return `${controleur.nom} ${controleur.prenom}`;
    if (controleur.nom) return controleur.nom;
    return "Contrôleur";
  };

  const getControllerDisplayName = (controller) => {
    if (!controller) return "";
    if (controller.nom_complet) return controller.nom_complet;
    if (controller.nom && controller.prenom)
      return `${controller.nom} ${controller.prenom}`;
    if (controller.nom) return controller.nom;
    return "Contrôleur";
  };

  const getUniqueControleursFromRondes = () => {
    const controleurs = rondes.map((r) => getControleurName(r.controleur));
    return [...new Set(controleurs)].sort();
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredRondes.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredRondes.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const generatePDFReport = async () => {
    if (filteredRondes.length === 0) {
      alert("Aucune donnée à exporter.");
      return;
    }

    setExportingPDF(true);
    try {
      const token = localStorage.getItem("access_token");

      // Détail de chaque ronde (sites visités, heure de passage, état du site
      // normal/incident) — nécessaire pour le rapport détaillé, absent de la
      // liste résumée déjà chargée en mémoire.
      const details = await Promise.all(
        filteredRondes.map((ronde) =>
          axios
            .get(`${API_BASE_URL}/rondes/detail_rondes.php?id=${ronde.id_ronde}`, {
              headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => res.data?.data || null)
            .catch(() => null),
        ),
      );

      const doc = new jsPDF("p", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      // En-tête : logo à gauche, nom/adresse/tél. de la société alignés sur la
      // même ligne (à droite du logo), titre sous l'ensemble, puis séparateur.
      const logoSize = 24;
      const logoTop = 8;
      let logoBottom = logoTop;
      if (societeInfo?.logo) {
        const format = societeInfo.logo.startsWith("data:image/png") ? "PNG"
          : societeInfo.logo.startsWith("data:image/webp") ? "WEBP"
          : "JPEG";
        try {
          doc.addImage(societeInfo.logo, format, 14, logoTop, logoSize, logoSize);
          logoBottom = logoTop + logoSize;
        } catch (e) {
          // Logo illisible (ex: SVG non supporté par jsPDF) : export sans logo plutôt que d'échouer.
        }
      }

      const infoX = 14 + logoSize + 6;
      let infoY = logoTop + 6;
      if (societeInfo?.nom) {
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text(societeInfo.nom, infoX, infoY);
        infoY += 5;
      }
      if (societeInfo?.infoLigne) {
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(100);
        doc.text(societeInfo.infoLigne, infoX, infoY);
        doc.setTextColor(0);
        infoY += 5;
      }

      let y = Math.max(logoBottom, infoY) + 6;

      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Rapport des Rondes", pageWidth / 2, y, { align: "center" });
      y += 5;

      const sepY = y + 3;
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.3);
      doc.line(14, sepY, pageWidth - 14, sepY);
      y = sepY + 7;

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(`Date de génération: ${new Date().toLocaleDateString("fr-FR")}`, 14, y);
      doc.text(`Heure: ${new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`, 14, y + 5);
      doc.text(`Total rondes: ${filteredRondes.length}`, 140, y);
      doc.text(
        `En cours: ${filteredRondes.filter((r) => r.statut === 1).length} | Terminées: ${filteredRondes.filter((r) => r.statut === 2).length}`,
        140,
        y + 5,
      );
      y += 15;

      // Un bloc par ronde : titre (date, contrôleur, statut) puis un tableau
      // détaillant chaque site visité (heure de passage, état, résumé incident).
      filteredRondes.forEach((ronde, idx) => {
        const detail = details[idx];

        if (y > pageHeight - 40) {
          doc.addPage();
          y = 20;
        }

        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(11, 60, 93);
        doc.text(
          `Ronde #${idx + 1} — ${formatDateTime(ronde.date_ronde)} — ${getControleurName(ronde.controleur)} — ${statusLabel(ronde.statut).text}`,
          14,
          y,
        );
        doc.setTextColor(0);
        y += 5;

        const sites = detail?.sites || [];

        if (sites.length === 0) {
          doc.setFontSize(9);
          doc.setFont("helvetica", "italic");
          doc.setTextColor(120);
          doc.text("Détail des sites indisponible pour cette ronde.", 14, y);
          doc.setTextColor(0);
          y += 8;
        } else {
          autoTable(doc, {
            startY: y,
            head: [["Site", "Heure de passage", "État", "Résumé"]],
            body: sites.map((s) => [
              s.nom || "-",
              s.scanne && s.date_scan ? formatDateTime(s.date_scan) : "Non scanné",
              s.etat === "incident" ? `Incident (${s.incident?.niveau_label || "?"})` : "RAS",
              s.incident
                ? [
                    `#${s.incident.numero_ticket} — ${s.incident.type || "Type non précisé"}`,
                    `${(s.incident.description || "").slice(0, 100)}${(s.incident.description || "").length > 100 ? "…" : ""}`,
                    `Action : ${s.incident.action || "Aucune action affectée"}`,
                    s.incident.resolu
                      ? `Résolu par ${s.incident.resolveur || "N/A"} le ${formatDateTime(s.incident.date_resolution)}`
                      : "Non résolu",
                  ].join("\n")
                : "-",
            ]),
            theme: "striped",
            headStyles: { fillColor: [30, 136, 200], textColor: 255, fontStyle: "bold", fontSize: 8 },
            styles: { fontSize: 8, cellPadding: 2 },
            columnStyles: {
              0: { cellWidth: 40 },
              1: { cellWidth: 32 },
              2: { cellWidth: 28 },
              3: { cellWidth: "auto" },
            },
            margin: { left: 14, right: 14 },
            didParseCell: (data) => {
              if (data.section === "body" && data.column.index === 2 && String(data.cell.raw).startsWith("Incident")) {
                data.cell.styles.textColor = [211, 47, 47];
                data.cell.styles.fontStyle = "bold";
              }
            },
          });
          y = doc.lastAutoTable.finalY + 10;
        }
      });

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(100);
        doc.text(`Page ${i} sur ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: "center" });
      }

      doc.save(
        `rapport_rondes_${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.pdf`,
      );
    } catch (error) {
      console.error("Erreur génération PDF rondes:", error);
      alert("Erreur lors de la génération du PDF.");
    } finally {
      setExportingPDF(false);
    }
  };

  const resetFilters = () => {
    setFilterControleur("");
    setFilterDateDebut("");
    setFilterDateFin("");
    setFilterStatut("");
    setSearchTerm("");
  };
  const hasActiveFilters = () =>
    filterControleur || filterDateDebut || filterDateFin || filterStatut || searchTerm;

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: "600" }}>
          Gestion des rondes
        </h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard" style={{ color: COLORS.primary }}>
                Tableau de bord
              </Link>
            </li>
            <li
              className="breadcrumb-item active"
              style={{ color: COLORS.secondary }}
            >
              Rondes
            </li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div
          className="card"
          style={{
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            border: "none",
          }}
        >
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5
                className="card-title mb-1"
                style={{
                  color: COLORS.primary,
                  borderBottom: `2px solid ${COLORS.lightBg}`,
                  paddingBottom: "16px",
                  width: "100%",
                }}
              >
                <div className="d-flex align-items-center">
                  <div
                    className="rounded-circle d-inline-flex align-items-center justify-content-center me-3"
                    style={{
                      width: "36px",
                      height: "36px",
                      backgroundColor: "#E3F2FD",
                      color: COLORS.primary,
                    }}
                  >
                    <i className="bi bi-shield-check"></i>
                  </div>
                  Liste des rondes
                </div>
              </h5>
              <div className="d-flex gap-2">
                {hasActiveFilters() && (
                  <button
                    className="btn fw-semibold"
                    onClick={resetFilters}
                    style={{
                      color: COLORS.primary,
                      borderColor: COLORS.primary,
                      backgroundColor: "transparent",
                      borderRadius: "8px",
                      padding: "10px 20px",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "#E3F2FD";
                      e.target.style.borderColor = COLORS.secondary;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "transparent";
                      e.target.style.borderColor = COLORS.primary;
                    }}
                  >
                    <i className="bi bi-arrow-clockwise me-2"></i>Réinitialiser
                  </button>
                )}
              </div>
            </div>

            {serverError && (
              <div
                className="alert alert-dismissible fade show mb-4"
                style={{
                  backgroundColor: "#ffebee",
                  borderColor: COLORS.danger,
                  color: "#c62828",
                  borderRadius: "8px",
                  borderLeft: `4px solid ${COLORS.danger}`,
                }}
              >
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">{serverError}</div>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setServerError("")}
                  ></button>
                </div>
              </div>
            )}

            {infoMsg && !loading && (
              <div
                className="alert alert-success alert-dismissible fade show mb-4"
                style={{
                  backgroundColor: "#e8f5e9",
                  borderColor: COLORS.success,
                  color: "#2e7d32",
                  borderRadius: "8px",
                  borderLeft: `4px solid ${COLORS.success}`,
                }}
              >
                <div className="d-flex align-items-center">
                  <i className="bi bi-info-circle me-2"></i>
                  <div className="flex-grow-1">{infoMsg}</div>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setInfoMsg("")}
                  ></button>
                </div>
              </div>
            )}

            {/* Filtres */}
            <div className="row mb-4">
              <div className="col-12">
                <div
                  className="card"
                  style={{
                    backgroundColor: "#E3F2FD",
                    border: "none",
                    borderRadius: "8px",
                  }}
                >
                  <div className="card-body py-3">
                    <div className="row g-3">
                      <div className="col-md-3">
                        <div className="input-group">
                          <span
                            className="input-group-text"
                            style={{
                              backgroundColor: COLORS.lightBg,
                              borderColor: "#e0e0e0",
                              color: COLORS.secondary,
                            }}
                          >
                            <i className="bi bi-person"></i>
                          </span>
                          <select
                            className="form-select"
                            value={filterControleur}
                            onChange={(e) =>
                              setFilterControleur(e.target.value)
                            }
                            style={{
                              borderColor: "#e0e0e0",
                              color: COLORS.darkText,
                            }}
                          >
                            <option value="">Tous les contrôleurs</option>
                            {controllersLoading ? (
                              <option disabled>Chargement...</option>
                            ) : controllers.length > 0 ? (
                              controllers.map((c) => (
                                <option
                                  key={c.id}
                                  value={getControllerDisplayName(c)}
                                >
                                  {getControllerDisplayName(c)}
                                </option>
                              ))
                            ) : (
                              getUniqueControleursFromRondes().map(
                                (name, i) => (
                                  <option key={i} value={name}>
                                    {name}
                                  </option>
                                ),
                              )
                            )}
                          </select>
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="input-group">
                          <span
                            className="input-group-text"
                            style={{
                              backgroundColor: COLORS.lightBg,
                              borderColor: "#e0e0e0",
                              color: COLORS.secondary,
                            }}
                          >
                            <i className="bi bi-calendar"></i>
                          </span>
                          <input
                            type="date"
                            className="form-control"
                            title="Date début"
                            placeholder="Date début"
                            value={filterDateDebut}
                            max={filterDateFin || undefined}
                            onChange={(e) => setFilterDateDebut(e.target.value)}
                            style={{
                              borderColor: "#e0e0e0",
                              color: COLORS.darkText,
                            }}
                          />
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="input-group">
                          <span
                            className="input-group-text"
                            style={{
                              backgroundColor: COLORS.lightBg,
                              borderColor: "#e0e0e0",
                              color: COLORS.secondary,
                            }}
                          >
                            <i className="bi bi-calendar-check"></i>
                          </span>
                          <input
                            type="date"
                            className="form-control"
                            title="Date fin"
                            placeholder="Date fin"
                            value={filterDateFin}
                            min={filterDateDebut || undefined}
                            onChange={(e) => setFilterDateFin(e.target.value)}
                            style={{
                              borderColor: "#e0e0e0",
                              color: COLORS.darkText,
                            }}
                          />
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="input-group">
                          <span
                            className="input-group-text"
                            style={{
                              backgroundColor: COLORS.lightBg,
                              borderColor: "#e0e0e0",
                              color: COLORS.secondary,
                            }}
                          >
                            <i className="bi bi-funnel"></i>
                          </span>
                          <select
                            className="form-select"
                            value={filterStatut}
                            onChange={(e) => setFilterStatut(e.target.value)}
                            style={{
                              borderColor: "#e0e0e0",
                              color: COLORS.darkText,
                            }}
                          >
                            <option value="">Tous les statuts</option>
                            <option value="En cours">En cours</option>
                            <option value="Terminée">Terminée</option>
                          </select>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="input-group">
                          <span
                            className="input-group-text"
                            style={{
                              backgroundColor: COLORS.lightBg,
                              borderColor: "#e0e0e0",
                              color: COLORS.secondary,
                            }}
                          >
                            <i className="bi bi-search"></i>
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Rechercher..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                              borderColor: "#e0e0e0",
                              color: COLORS.darkText,
                            }}
                          />
                          {searchTerm && (
                            <button
                              type="button"
                              className="btn"
                              onClick={() => setSearchTerm("")}
                              style={{
                                backgroundColor: COLORS.lightBg,
                                borderColor: "#e0e0e0",
                                color: COLORS.darkText,
                              }}
                            >
                              <i className="bi bi-x"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 d-flex justify-content-between align-items-center">
                      <div className="text-muted small">
                        <i className="bi bi-info-circle me-1"></i>
                        <span
                          className="badge fw-semibold me-2"
                          style={{
                            backgroundColor: COLORS.lightBg,
                            color: COLORS.primary,
                            padding: "4px 12px",
                            borderRadius: "6px",
                          }}
                        >
                          {filteredRondes.length} ronde(s)
                          {filteredRondes.length !== rondes.length &&
                            ` sur ${rondes.length} au total`}
                        </span>
                        {hasActiveFilters() && (
                          <span>
                            <i className="bi bi-funnel me-1"></i>Filtres actifs:
                            {filterControleur && (
                              <span className="badge bg-info ms-1">
                                Contrôleur
                              </span>
                            )}
                            {filterDateDebut && (
                              <span className="badge bg-info ms-1">
                                Date début
                              </span>
                            )}
                            {filterDateFin && (
                              <span className="badge bg-info ms-1">
                                Date fin
                              </span>
                            )}
                            {filterStatut && (
                              <span className="badge bg-info ms-1">Statut</span>
                            )}
                            {searchTerm && (
                              <span className="badge bg-info ms-1">
                                Recherche
                              </span>
                            )}
                          </span>
                        )}
                      </div>
                      <button
                        className="btn fw-semibold"
                        onClick={generatePDFReport}
                        disabled={loading || exportingPDF || filteredRondes.length === 0}
                        style={{
                          color: COLORS.white,
                          backgroundColor: COLORS.danger,
                          borderColor: COLORS.danger,
                          borderRadius: "8px",
                          padding: "8px 16px",
                          fontSize: "0.9rem",
                        }}
                        onMouseEnter={(e) => {
                          if (!loading && !exportingPDF && filteredRondes.length > 0)
                            e.currentTarget.style.backgroundColor = "#d32f2f";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = COLORS.danger;
                        }}
                      >
                        {exportingPDF ? (
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        ) : (
                          <i className="bi bi-file-earmark-pdf-fill me-2"></i>
                        )}
                        {exportingPDF ? "Génération..." : "Générer PDF"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-5">
                <div
                  className="spinner-border"
                  role="status"
                  style={{ color: COLORS.primary }}
                >
                  <span className="visually-hidden">Chargement...</span>
                </div>
                <p className="mt-2" style={{ color: COLORS.darkText }}>
                  Chargement des rondes...
                </p>
              </div>
            ) : rondes.length === 0 ? (
              <div className="text-center py-5">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-4"
                  style={{
                    width: "80px",
                    height: "80px",
                    backgroundColor: "#F5F7FA",
                    color: "#B0BEC5",
                  }}
                >
                  <i className="bi bi-shield-check fs-1"></i>
                </div>
                <h6 className="mb-3" style={{ color: COLORS.primary }}>
                  Aucune ronde trouvée
                </h6>
                <button
                  onClick={fetchRondes}
                  className="btn fw-semibold mt-3"
                  style={{
                    color: COLORS.primary,
                    borderColor: COLORS.primary,
                    backgroundColor: "transparent",
                    borderRadius: "8px",
                    padding: "10px 24px",
                  }}
                >
                  <i className="bi bi-arrow-clockwise me-2"></i>Actualiser
                </button>
              </div>
            ) : filteredRondes.length === 0 ? (
              <div className="text-center py-5">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-4"
                  style={{
                    width: "80px",
                    height: "80px",
                    backgroundColor: "#F5F7FA",
                    color: "#B0BEC5",
                  }}
                >
                  <i className="bi bi-search fs-1"></i>
                </div>
                <h6 className="mb-3" style={{ color: COLORS.primary }}>
                  Aucun résultat
                </h6>
                <button
                  className="btn fw-semibold mt-3"
                  onClick={resetFilters}
                  style={{
                    color: COLORS.primary,
                    borderColor: COLORS.primary,
                    backgroundColor: "transparent",
                    borderRadius: "8px",
                    padding: "10px 24px",
                  }}
                >
                  <i className="bi bi-x-lg me-2"></i>Effacer les filtres
                </button>
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr
                        style={{
                          backgroundColor: COLORS.lightBg,
                          borderBottom: `2px solid ${COLORS.accent}`,
                        }}
                      >
                        {[
                          "Date",
                          "Contrôleur",
                          "Nombre de sites",
                          "Statut",
                          "Actions",
                        ].map((h, i) => (
                          <th
                            key={h}
                            style={{
                              color: COLORS.primary,
                              fontWeight: "600",
                              padding: "16px",
                              textAlign: i === 4 ? "center" : "left",
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.map((ronde, index) => {
                        const statut = statusLabel(ronde.statut);
                        const controleurName = getControleurName(
                          ronde.controleur,
                        );
                        const rowId =
                          ronde.id_ronde || ronde.id || `row-${index}`;
                        const isLoadingThis = mapLoading === rowId;

                        return (
                          <tr
                            key={rowId}
                            style={{
                              borderBottom: `1px solid ${COLORS.lightBg}`,
                            }}
                          >
                            {/* Date */}
                            <td style={{ padding: "16px" }}>
                              <div className="d-flex align-items-center">
                                <div
                                  className="rounded-circle d-flex align-items-center justify-content-center me-3"
                                  style={{
                                    width: "36px",
                                    height: "36px",
                                    backgroundColor: "#E3F2FD",
                                    color: COLORS.primary,
                                    fontSize: "0.9rem",
                                    flexShrink: 0,
                                  }}
                                >
                                  <i className="bi bi-calendar"></i>
                                </div>
                                <div>
                                  <div
                                    style={{
                                      color: COLORS.darkText,
                                      fontWeight: "500",
                                    }}
                                  >
                                    {formatDate(ronde.date_ronde)}
                                  </div>
                                  <small
                                    className="text-muted"
                                    style={{ fontSize: "0.8rem" }}
                                  >
                                    {new Date(
                                      ronde.date_ronde,
                                    ).toLocaleTimeString("fr-FR", {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })}
                                  </small>
                                </div>
                              </div>
                            </td>

                            {/* Contrôleur */}
                            <td style={{ padding: "16px" }}>
                              <div className="d-flex align-items-center">
                                <div
                                  className="text-white rounded-circle d-flex align-items-center justify-content-center fw-semibold me-3"
                                  style={{
                                    width: "40px",
                                    height: "40px",
                                    fontSize: "14px",
                                    backgroundColor: COLORS.primary,
                                    flexShrink: 0,
                                  }}
                                >
                                  {controleurName.charAt(0).toUpperCase()}
                                </div>
                                <div
                                  style={{
                                    color: COLORS.darkText,
                                    fontWeight: "500",
                                  }}
                                >
                                  {controleurName}
                                </div>
                              </div>
                            </td>

                            {/* Nb sites */}
                            <td style={{ padding: "16px" }}>
                              <span
                                className="badge fw-semibold"
                                style={{
                                  backgroundColor: COLORS.infoLight,
                                  color: COLORS.info,
                                  padding: "8px 12px",
                                  borderRadius: "6px",
                                  fontSize: "0.9rem",
                                }}
                              >
                                <i className="bi bi-building me-1"></i>
                                {ronde.site_count || 0}
                              </span>
                            </td>

                            {/* Statut */}
                            <td style={{ padding: "16px" }}>
                              <span
                                className="badge fw-semibold"
                                style={{
                                  ...statut.style,
                                  padding: "8px 16px",
                                  borderRadius: "6px",
                                  fontSize: "0.85rem",
                                }}
                              >
                                {ronde.statut === 1 ? (
                                  <>
                                    <i className="bi bi-clock me-1"></i>En cours
                                  </>
                                ) : (
                                  <>
                                    <i className="bi bi-check-circle me-1"></i>
                                    Terminée
                                  </>
                                )}
                              </span>
                            </td>

                            {/* Actions */}
                            <td
                              style={{ padding: "16px", textAlign: "center" }}
                            >
                              <div className="d-flex gap-2 justify-content-center">
                                {/* Détails */}
                                <button
                                  className="btn btn-sm fw-semibold"
                                  onClick={() => handleViewDetails(ronde)}
                                  title="Voir les détails"
                                  style={{
                                    color: COLORS.primary,
                                    borderColor: COLORS.primary,
                                    backgroundColor: "transparent",
                                    borderRadius: "6px",
                                    padding: "6px 14px",
                                    minWidth: "44px",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                      COLORS.primary;
                                    e.currentTarget.style.color = "white";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                      "transparent";
                                    e.currentTarget.style.color =
                                      COLORS.primary;
                                  }}
                                >
                                  <i className="bi bi-eye"></i>
                                </button>

                                {/* Carte — charge les sites à la volée */}
                                <button
                                  className="btn btn-sm fw-semibold"
                                  onClick={() => handleShowMap(ronde)}
                                  disabled={isLoadingThis}
                                  title="Voir les sites sur la carte"
                                  style={{
                                    color: COLORS.info,
                                    borderColor: COLORS.info,
                                    backgroundColor: "transparent",
                                    borderRadius: "6px",
                                    padding: "6px 14px",
                                    minWidth: "44px",
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isLoadingThis) {
                                      e.currentTarget.style.backgroundColor =
                                        COLORS.info;
                                      e.currentTarget.style.color = "white";
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                      "transparent";
                                    e.currentTarget.style.color = COLORS.info;
                                  }}
                                >
                                  {isLoadingThis ? (
                                    <span
                                      className="spinner-border spinner-border-sm"
                                      role="status"
                                    />
                                  ) : (
                                    <i className="bi bi-geo-alt"></i>
                                  )}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-4">
                    <div className="d-flex align-items-center">
                      <span className="me-2" style={{ color: COLORS.darkText }}>
                        Afficher :
                      </span>
                      <select
                        className="form-select form-select-sm w-auto"
                        value={itemsPerPage}
                        onChange={handleItemsPerPageChange}
                        style={{
                          borderColor: "#e0e0e0",
                          color: COLORS.darkText,
                          borderRadius: "6px",
                        }}
                      >
                        {[5, 10, 25, 50, 100].map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                      <span
                        className="ms-3 badge fw-semibold"
                        style={{
                          backgroundColor: "#E3F2FD",
                          color: COLORS.primary,
                          padding: "4px 12px",
                          borderRadius: "6px",
                        }}
                      >
                        {filteredRondes.length} ronde(s)
                        {filteredRondes.length !== rondes.length
                          ? " (filtrées)"
                          : ""}
                      </span>
                    </div>
                    <nav>
                      <ul className="pagination pagination-sm mb-0">
                        <li
                          className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                        >
                          <button
                            className="page-link fw-semibold"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            style={{
                              color:
                                currentPage === 1 ? "#B0BEC5" : COLORS.primary,
                              borderColor: "#e0e0e0",
                              borderRadius: "6px",
                              margin: "0 2px",
                            }}
                          >
                            <i className="bi bi-chevron-left"></i>
                          </button>
                        </li>
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                          .filter(
                            (p) =>
                              totalPages <= 7 ||
                              p === 1 ||
                              p === totalPages ||
                              (p >= currentPage - 1 && p <= currentPage + 1),
                          )
                          .map((page, idx, arr) => (
                            <React.Fragment key={page}>
                              {idx > 0 && page > arr[idx - 1] + 1 && (
                                <li className="page-item disabled">
                                  <span
                                    className="page-link"
                                    style={{ borderColor: "#e0e0e0" }}
                                  >
                                    ...
                                  </span>
                                </li>
                              )}
                              <li
                                className={`page-item ${currentPage === page ? "active" : ""}`}
                              >
                                <button
                                  className="page-link fw-semibold"
                                  onClick={() => handlePageChange(page)}
                                  style={{
                                    backgroundColor:
                                      currentPage === page
                                        ? COLORS.primary
                                        : "transparent",
                                    borderColor:
                                      currentPage === page
                                        ? COLORS.primary
                                        : "#e0e0e0",
                                    color:
                                      currentPage === page
                                        ? "white"
                                        : COLORS.primary,
                                    borderRadius: "6px",
                                    margin: "0 2px",
                                  }}
                                >
                                  {page}
                                </button>
                              </li>
                            </React.Fragment>
                          ))}
                        <li
                          className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                        >
                          <button
                            className="page-link fw-semibold"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            style={{
                              color:
                                currentPage === totalPages
                                  ? "#B0BEC5"
                                  : COLORS.primary,
                              borderColor: "#e0e0e0",
                              borderRadius: "6px",
                              margin: "0 2px",
                            }}
                          >
                            <i className="bi bi-chevron-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* ── MapModal — alimenté par les sites fetchés à la volée ── */}
      {showMap && selectedRonde && (
        <MapModal
          isOpen={showMap}
          onClose={() => {
            setShowMap(false);
            setSelectedSites([]);
            setSelectedRonde(null);
          }}
          title={`Sites de la ronde${selectedRonde.titre ? ` — ${selectedRonde.titre}` : ` #${selectedRonde.id_ronde || selectedRonde.id}`}`}
          latitude={selectedSites[0]?.latitude || null}
          longitude={selectedSites[0]?.longitude || null}
          allRondes={selectedSites.map((s) => ({
            latitude: s.latitude,
            longitude: s.longitude,
            titre: s.nom,
            site: { nom: s.nom },
            statut: selectedRonde.statut,
            scanne: s.scanne,
          }))}
          showAllRondes={true}
          initialZoom={12}
        />
      )}

      <style>{`
        .btn:focus, .form-control:focus, .page-link:focus, .form-select:focus {
          outline: none; box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        .table-hover tbody tr:hover { background-color: ${COLORS.lightBg}; }
        .table th { text-transform: uppercase; letter-spacing: 0.5px; font-size: 0.85rem; }
        .table td { vertical-align: middle; }
        @media (max-width: 768px) {
          .card { margin: 0 -16px; border-radius: 0 !important; }
          .pagetitle h1 { font-size: 1.5rem; }
        }
      `}</style>
    </Layout>
  );
};

export default RoundList;
